/*       */

import { label } from 'app/utils/designer/form/settings/common/commonBase';
import { confirmationBuilder } from 'app/utils/designer/form/settings/common/commonGeneral';
import { onLoad, onClick, isVisible, isDisabled } from 'app/utils/designer/form/settings/common/commonEvents';
import { help } from 'app/utils/designer/form/settings/common/commonHelpers';
import { flexGrow } from 'app/utils/designer/form/settings/common/commonStyles';

const panels = settingsValues => [
    {
        header: 'Base',
        children: [
            label,
            {
                type: 'iconSelect',
                properties: {
                    label: 'Select icon',
                    name: 'properties.icon',
                    clearable: true,
                },
            },            
        ],
    },
    {
        header: 'Events',
        expanded: true,
        children: [
            { type: 'boolean', properties: { label: 'Validate form before action', name: 'properties.validateBefore' } },
            {
                type: 'typeahead',
                properties: {
                    label: 'On click action type',
                    name: 'properties.actionType',
                    valueField: 'value',
                    clearable: false,
                    options: [
                        { value: 'function', label: 'Function' },
                        { value: 'internal', label: 'Internal link' },
                        { value: 'external', label: 'External link' },
                    ],
                    onChange: String(function(event) {
                        const { value, name } = event.target;

                        if (value === 'internal') {
                            return [
                                { name: 'properties.href', value: null },
                                { name, value },
                            ];
                        }
                        if (value === 'external') {
                            return [
                                { name: 'properties.to', value: null },
                                { name, value },
                            ];
                        }
                        return [
                            { name: 'properties.href', value: null },
                            { name: 'properties.to', value: null },
                            { name, value },
                        ];
                    }),
                },
            },
            ...confirmationBuilder,
            {
                type: 'text',
                properties: {
                    label: 'Link',
                    name: 'properties.to',
                    isVisible: ({ properties }) => properties && properties.actionType === 'internal',
                    // the onChange function must be a string to run in the service worker
                    onChange: String(function(event) {
                        const { value, name } = event.target;
                        return [
                            { name: 'properties.href', value: null },
                            { name, value },
                        ];
                    }),
                },
                constraints: {
                    required: ({ properties }) => properties?.actionType === 'internal',
                },
            },
            {
                type: 'text',
                properties: {
                    label: 'Link',
                    name: 'properties.href',
                    isVisible: ({ properties }) => properties && properties.actionType === 'external',
                    // the onChange function must be a string to run in the service worker
                    onChange: String(function(event) {
                        const { value, name } = event.target;
                        return [
                            { name: 'properties.to', value: null },
                            { name, value },
                        ];
                    }),
                },
                constraints: {
                    required: ({ properties }) => properties?.actionType === 'external',
                },
            },
            {
                type: 'typeahead',
                properties: {
                    label: 'Link target',
                    name: 'properties.target',
                    valueField: 'value',
                    clearable: false,
                    options: [
                        { value: '_blank', label: 'New tab' },
                        { value: '_self', label: 'Same tab' },
                        { value: 'dialog', label: 'Dialog' },
                        { value: 'newWindow', label: 'New Window' },
                    ],
                    onChange: String(function(event) {
                        const { value, name } = event.target;
                        const resetWindowSize = [
                            {name : 'properties.windowHeight', value: null},
                            {name : 'properties.windowWidth', value: null},
                        ];
                        const resetDialogSize = [
                            {name : 'properties.dialogHeight', value: null},
                            {name : 'properties.dialogWidth', value: null},
                        ];
                        if (value === '_self' || value === '_blank') {
                            return [
                                ...resetWindowSize,
                                ...resetDialogSize,
                                { name, value },
                            ];
                        }
                        if (value === 'dialog') {
                            return [
                                ...resetWindowSize,
                                { name, value },
                            ];
                        }
                        if (value === 'newWindow') {
                            return [
                                ...resetDialogSize,
                                { name, value },
                            ];
                        }
                        return [
                            ...resetWindowSize,
                            ...resetDialogSize,
                            { name, value },
                        ];
                    }),
                    isVisible: ({ properties }) => properties && properties.actionType === 'external',
                },
            },
            {
                type: 'number',
                properties: {
                    isVisible: ({ properties }) => properties?.target === 'newWindow' && properties?.actionType === 'external',
                    label: 'Window width',
                    name: 'properties.windowWidth',
                },
                constraints: { 
                    custom: data => {
                        if(data?.properties?.windowWidth < 320){
                            return { 
                                format: { 
                                    pattern: /somefaketest/, 
                                    message: `Min window width 320px.` } };
                        }
                        return {};
                    }
                },
            },
            {
                type: 'number',
                properties: {
                    isVisible: ({ properties }) => properties?.target === 'newWindow' && properties?.actionType === 'external',
                    label: 'Window height',
                    name: 'properties.windowHeight',
                },
                constraints: { 
                    custom: data => {
                        if(data?.properties?.windowHeight < 480){
                            return { 
                                format: { 
                                    pattern: /somefaketest/, 
                                    message: `Min window height 480px.` } };
                        }
                        return {};
                    }
                },
            },
            {
                type: 'number',
                properties: {
                    isVisible: ({ properties }) => properties?.target === 'dialog' && properties?.actionType === 'external',
                    label: 'Dialog width',
                    name: 'properties.dialogWidth',
                },
                constraints: { 
                    custom: data => {
                        if(data?.properties?.dialogWidth < 320){
                            return { 
                                format: { 
                                    pattern: /somefaketest/, 
                                    message: `Min dialog width 320px.` } };
                        }
                        return {};
                    }
                },
            },
            {
                type: 'number',
                properties: {
                    isVisible: ({ properties }) => properties?.target === 'dialog' && properties?.actionType === 'external',
                    label: 'Dialog height',
                    name: 'properties.dialogHeight',
                },
                constraints: { 
                    custom: data => {
                        if(data?.properties?.dialogHeight < 480){
                            return { 
                                format: { 
                                    pattern: /somefaketest/, 
                                    message: `Min dialog height 480px.` } };
                        }
                        return {};
                    }
                },
            },
            onLoad,
            onClick,
            isVisible,
            settingsValues?.viewType !== 'class' && isDisabled,
        ].filter(Boolean),
    },
    {
        header: 'Styles',
        children: [
            {
                type: 'typeahead',
                properties: {
                    label: 'Color',
                    name: 'properties.color',
                    valueField: 'value',
                    clearable: false,
                    options: [
                        { value: 'primary', label: 'primary' },
                        { value: 'secondary', label: 'secondary' },
                    ],
                },
            },
            {
                type: 'typeahead',
                properties: {
                    label: 'Variant',
                    name: 'properties.variant',
                    valueField: 'value',
                    clearable: false,
                    options: [
                        { value: 'text', label: 'Text' },
                        { value: 'outlined', label: 'Outlined' },
                        { value: 'contained', label: 'Contained' },
                    ],
                },
            },
            { type: 'boolean', properties: { label: 'Full width', name: 'properties.fullWidth' } },
            { ...flexGrow },
        ],
    },
    {
        header: 'Helpers',
        children: [help],
    },
];

export default {
    defaults: {
        actionType: 'function',
        fullWidth: true,
        variant: 'contained',
        color: 'primary',
        formFieldProps: { flexGrow: 1 },
    },
    panels: settingValues => panels(settingValues),
};
